import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, finalize, takeUntil } from 'rxjs';
import { AppModeService } from 'src/app/services/app-mode.service';
import {
  AddressPayload,
  AddressService,
} from 'src/app/services/address/address.service';
import { AuthService } from 'src/app/services/auth/auth.service';

@Component({
  selector: 'app-address-form-page',
  templateUrl: './address-form-page.component.html',
  styleUrls: ['./address-form-page.component.css'],
})
export class AddressFormPageComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private addressId: string | null = null;

  addressForm: FormGroup;
  isEditMode = false;

  isSaving = false;
  errorMessage = '';

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    public readonly appModeService: AppModeService,
    private readonly authService: AuthService,
    private readonly addressService: AddressService,
  ) {
    this.addressForm = this.formBuilder.group({
      streetAddress: ['', Validators.required],
      region: ['', Validators.required],
      city: ['', Validators.required],
      postalCode: [''],
    });
  }

  ngOnInit(): void {
    const lang = localStorage.getItem('lang') || 'en';

    if (!this.appModeService.isApp()) {
      this.router.navigateByUrl(`/${lang}/home`);
      return;
    }

    if (!this.authService.isLoggedIn()) {
      this.router.navigateByUrl(`/${lang}/home`);
      return;
    }

    this.addressId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!this.addressId;

    if (this.isEditMode && this.addressId) {
      this.addressService
        .getAddresses()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (addresses) => {
            const selected = addresses.find(
              (item) => item.id === this.addressId,
            );
            if (!selected) {
              this.goBack();
              return;
            }

            this.addressForm.patchValue({
              streetAddress: selected.streetAddress,
              region: selected.region,
              city: selected.city,
              postalCode: selected.postalCode,
            });
          },
          error: () => {
            this.errorMessage = 'PROFILE.ADDRESSES.ERROR_LOAD_FAILED';
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  goBack(): void {
    const lang = localStorage.getItem('lang') || 'en';
    this.router.navigateByUrl(`/${lang}/profile/addresses`);
  }

  onSubmit(): void {
    if (this.addressForm.invalid) {
      this.addressForm.markAllAsTouched();
      return;
    }

    const payload: AddressPayload = this.addressForm.value;
    this.isSaving = true;
    this.errorMessage = '';

    const request$ =
      this.isEditMode && this.addressId
        ? this.addressService.updateAddress(this.addressId, payload)
        : this.addressService.createAddress(payload);

    request$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSaving = false;
        }),
      )
      .subscribe({
        next: () => {
          this.goBack();
        },
        error: () => {
          this.errorMessage = 'PROFILE.ADDRESSES.ERROR_SAVE_FAILED';
        },
      });
  }

  controlHasError(
    controlName: 'streetAddress' | 'region' | 'city' | 'postalCode',
  ): boolean {
    const control = this.addressForm.get(controlName);
    return !!control && control.invalid && (control.dirty || control.touched);
  }
}
